import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Tooltip,
    Paper,
    CircularProgress,
    useTheme,
    alpha
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, CAP } from '../utils/permissions';
import partiesService, { Party } from '../services/partiesService';
import AccessDenied from '../components/AccessDenied';
import { useFormFieldErrors, REQUIRED_FIELDS_MESSAGE } from '../hooks/useFormFieldErrors';
import FormValidationAlert from '../components/FormValidationAlert';

// Nautilus Console Design System Colors
const NAUTILUS = {
    deepOcean: '#0f2134',
    sand: '#ccba9b',
    abyssal: '#0a1622',
};

const ThirdPartiesPage: React.FC = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { user } = useAuth();
    const [parties, setParties] = useState<Party[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Dialog states
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState<Omit<Party, 'id'>>({ name: '', trigram: '', contact: '', role_default: '' });

    // Delete confirmation dialog
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [partyToDelete, setPartyToDelete] = useState<Party | null>(null);
    const fieldErrors = useFormFieldErrors();

    // Design System Tokens
    const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
    const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';
    const headerBg = isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.deepOcean, 0.04);

    useEffect(() => {
        loadParties();
    }, []);

    const loadParties = async () => {
        try {
            setLoading(true);
            const data = await partiesService.getParties();
            setParties(data);
            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to load parties');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreateDialog = () => {
        setFormData({ name: '', trigram: '', contact: '', role_default: '' });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setFormData({ name: '', trigram: '', contact: '', role_default: '' });
        fieldErrors.clear();
    };

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            fieldErrors.show(REQUIRED_FIELDS_MESSAGE, ['name']);
            return;
        }
        try {
            setLoading(true);
            fieldErrors.clear();

            await partiesService.createParty(formData);
            setSuccess('Party created successfully');

            handleCloseDialog();
            await loadParties();

            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            fieldErrors.show(err.response?.data?.error || 'Failed to create party');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDeleteDialog = (party: Party) => {
        setPartyToDelete(party);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setPartyToDelete(null);
        setOpenDeleteDialog(false);
    };

    const handleDelete = async () => {
        if (!partyToDelete) return;

        try {
            setLoading(true);
            setError(null);
            await partiesService.deleteParty(partyToDelete.id);
            setSuccess('Party deleted successfully');
            handleCloseDeleteDialog();
            await loadParties();
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to delete party');
            handleCloseDeleteDialog();
        } finally {
            setLoading(false);
        }
    };

    const canManageParties = hasCapability(user, CAP.MANAGE_THIRD_PARTIES);

    if (!canManageParties) {
        return (
            <AccessDenied
                title="Access denied"
                message="You do not have permission to manage third parties."
            />
        );
    }

    if (loading && parties.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress sx={{ color: NAUTILUS.sand }} />
            </Box>
        );
    }

    return (
        <Box>
            {/* Page Header */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={4}
                sx={{
                    pb: 2,
                    borderBottom: `1px solid ${panelBorder}`,
                }}
            >
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                        color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
                    }}
                >
                    Third Parties Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreateDialog}
                    sx={{
                        bgcolor: NAUTILUS.sand,
                        color: NAUTILUS.deepOcean,
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        px: 3,
                        '&:hover': {
                            bgcolor: alpha(NAUTILUS.sand, 0.85),
                        },
                    }}
                >
                    New Party
                </Button>
            </Box>

            {error && (
                <Alert
                    severity="error"
                    sx={{
                        mb: 3,
                        border: `1px solid ${theme.palette.error.main}`,
                        borderRadius: 1,
                    }}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {success && (
                <Alert
                    severity="success"
                    sx={{
                        mb: 3,
                        border: `1px solid ${theme.palette.success.main}`,
                        borderRadius: 1,
                    }}
                    onClose={() => setSuccess(null)}
                >
                    {success}
                </Alert>
            )}

            {/* Data Panel */}
            <Card
                sx={{
                    bgcolor: surfaceBg,
                    border: `1px solid ${panelBorder}`,
                    borderRadius: 1,
                    boxShadow: 'none',
                }}
            >
                <CardContent sx={{ p: 0 }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        bgcolor: headerBg,
                                        borderBottom: `1px solid ${panelBorder}`,
                                    }}
                                >
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                            letterSpacing: '0.05em',
                                            textTransform: 'uppercase',
                                            color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                                            borderBottom: 'none',
                                            py: 1.5,
                                        }}
                                    >
                                        Name
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                            letterSpacing: '0.05em',
                                            textTransform: 'uppercase',
                                            color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                                            borderBottom: 'none',
                                            py: 1.5,
                                        }}
                                    >
                                        Trigram
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                            letterSpacing: '0.05em',
                                            textTransform: 'uppercase',
                                            color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                                            borderBottom: 'none',
                                            py: 1.5,
                                        }}
                                    >
                                        Contact
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                            letterSpacing: '0.05em',
                                            textTransform: 'uppercase',
                                            color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                                            borderBottom: 'none',
                                            py: 1.5,
                                        }}
                                    >
                                        Default Role
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                            letterSpacing: '0.05em',
                                            textTransform: 'uppercase',
                                            color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                                            borderBottom: 'none',
                                            py: 1.5,
                                        }}
                                    >
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {parties.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={5}
                                            align="center"
                                            sx={{ borderBottom: 'none' }}
                                        >
                                            <Typography color="text.secondary" py={4}>
                                                No parties found.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    parties.map((party, index) => (
                                        <TableRow
                                            key={party.id}
                                            sx={{
                                                '&:hover': {
                                                    bgcolor: isDark
                                                        ? alpha(NAUTILUS.sand, 0.05)
                                                        : alpha(NAUTILUS.deepOcean, 0.02),
                                                },
                                                borderBottom: index === parties.length - 1
                                                    ? 'none'
                                                    : `1px solid ${alpha(panelBorder, 0.5)}`,
                                            }}
                                        >
                                            <TableCell sx={{ borderBottom: 'none', py: 1.5 }}>
                                                {party.name}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    borderBottom: 'none',
                                                    py: 1.5,
                                                    fontFamily: 'monospace',
                                                    letterSpacing: '0.05em',
                                                }}
                                            >
                                                {party.trigram || '—'}
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: 'none', py: 1.5 }}>
                                                {party.contact || '—'}
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: 'none', py: 1.5 }}>
                                                {party.role_default || '—'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ borderBottom: 'none', py: 1.5 }}>
                                                <Tooltip title="Delete party">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenDeleteDialog(party)}
                                                        sx={{
                                                            color: theme.palette.error.main,
                                                            '&:hover': {
                                                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                                            },
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Create Dialog - Overlay Level */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: surfaceBg,
                        border: `1px solid ${panelBorder}`,
                        borderRadius: 1,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        borderBottom: `1px solid ${panelBorder}`,
                        fontWeight: 600,
                        letterSpacing: '-0.01em',
                    }}
                >
                    Create New Party
                </DialogTitle>
                <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ pt: 1 }}>
                        <TextField
                            fullWidth
                            label="Name"
                            value={formData.name}
                            error={fieldErrors.invalid('name')}
                            onChange={(e) => {
                                fieldErrors.clearField('name');
                                setFormData({ ...formData, name: e.target.value });
                            }}
                            required
                            margin="normal"
                            autoFocus
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                        />
                        <TextField
                            fullWidth
                            label="Trigram"
                            value={formData.trigram}
                            onChange={(e) => setFormData({ ...formData, trigram: e.target.value })}
                            margin="normal"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                        />
                        <TextField
                            fullWidth
                            label="Contact (Email)"
                            value={formData.contact}
                            onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                            margin="normal"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                        />
                        <TextField
                            fullWidth
                            label="Default Role"
                            value={formData.role_default}
                            onChange={(e) => setFormData({ ...formData, role_default: e.target.value })}
                            margin="normal"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}` }}>
                    <Button
                        onClick={handleCloseDialog}
                        sx={{ color: 'text.secondary' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading}
                        sx={{
                            bgcolor: NAUTILUS.sand,
                            color: NAUTILUS.deepOcean,
                            fontWeight: 600,
                            '&:hover': {
                                bgcolor: alpha(NAUTILUS.sand, 0.85),
                            },
                            '&:disabled': {
                                bgcolor: alpha(NAUTILUS.sand, 0.3),
                            },
                        }}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDeleteDialog}
                onClose={handleCloseDeleteDialog}
                PaperProps={{
                    sx: {
                        bgcolor: surfaceBg,
                        border: `1px solid ${panelBorder}`,
                        borderRadius: 1,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        borderBottom: `1px solid ${panelBorder}`,
                        fontWeight: 600,
                    }}
                >
                    Confirm Delete
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Typography>
                        Are you sure you want to delete the party "{partyToDelete?.name}"?
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 1, fontSize: '0.875rem' }}>
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}` }}>
                    <Button
                        onClick={handleCloseDeleteDialog}
                        sx={{ color: 'text.secondary' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        disabled={loading}
                        sx={{ fontWeight: 600 }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ThirdPartiesPage;
